// ================================================================
//  MetricsExplorerDialogController
//  Time-series explorer for a biz interceptor's captured/uploaded/
//  dropped/filtered frame counts and received/uploaded byte volume —
//  built analogous to AgentStatsDialogController (same rzslider-based
//  time-range/interval pickers, same preset buttons, same pagination-
//  as-resolution pattern, same Chart.js line chart), backed by
//  InterceptorTimeSeriesController's already-existing windowed
//  aggregation API (avg/max/min per dimension per bucket).
//
//  Live Monitoring works the same way as AgentStatsDialogController's:
//  reuses the existing historical endpoints (no separate real-time API —
//  just the time-series + time-series/time-range pair already used for
//  browsing history), refreshing on a setInterval paced to the
//  interceptor's own /sync cadence (getInterceptorSyncInterval), re-
//  anchoring the 20-point sliding window to the server's latest ingested
//  timestamp every tick. SLA threshold overlays (Pressure / Agent Latency)
//  work the same way as AgentStatsDialogController's too (2026-09-20 parity
//  pass) — SET/RESET lines built from this interceptor's own monitoring_config,
//  fetched separately since the list row this dialog opens from doesn't carry
//  it (see the SLA THRESHOLD OVERLAYS section below). Differences that remain
//  from the agent dialog it's modeled on:
//   - An environment scope selector (vertical list, same pattern as
//     ConsolidatedStatsDialogController) — the time-series API accepts
//     an optional environment_id, so metrics can be viewed for one
//     environment or summed across all of them. Pre-selected to the
//     environment whose live-status dot was clicked to open this
//     dialog, defaulting to "All Environments" otherwise.
//
//  Locals injected:
//    - interceptor : the list row (biz_interceptor_id, environments[], ...)
//    - environment : the specific environment clicked (or null/undefined
//                     when opened from a trigger with no environment context)
// ================================================================

angular.module('bizmetryApp').controller('MetricsExplorerDialogController', function (
    $scope, $mdDialog, $timeout, interceptor, environment, BizInterceptorService, ConfigurationService, LiveChartViewport
) {
    // Live charts show twenty buckets at the live aggregation interval.
    const LIVE_POINT_COUNT = 20;
    $scope.interceptor = interceptor;
    $scope.loading = true;
    $scope.timeRangeLoading = true;
    $scope.aggregatedData = [];
    $scope.totalIntervals = 0;
    $scope.noDataMessage = '';
    $scope.isLoadingChart = false;
    $scope.isChangingInterval = false;
    $scope.activeTimePreset = null;

    $scope.page = 0;
    $scope.totalPages = 0;
    $scope.pageSizeOptions = [15, 30, 45, 60];

    // ========================================
    // LIVE MONITORING
    // ========================================

    $scope.isLiveMode = false;
    var liveMonitoringInterval = null;

    // The sliding window's duration in ms — set once when Live Monitoring
    // turns on (20 * the interceptor's own sync interval), read by both
    // toggleLiveMode and loadMetrics so they compute the same window.
    var liveWindowMs = null;

    // ========================================
    // ENVIRONMENT SCOPE
    // ========================================

    $scope.envOptions = [{ id: 'all', name: 'All Environments' }].concat(
        (interceptor.environments || []).map(function (env) { return { id: env.id, name: env.env_name }; })
    );
    $scope.selectedEnvId = (environment && environment.id) ? environment.id : 'all';

    function currentEnvironmentId() {
        return $scope.selectedEnvId === 'all' ? null : $scope.selectedEnvId;
    }

    $scope.selectEnv = function (envId) {
        if ($scope.selectedEnvId === envId) { return; }
        if ($scope.isLiveMode) {
            // Switching scope mid-stream would be reading a different
            // series while the sliding window is still ticking against the
            // old one — turn it off first so the toggle's own on/off path
            // (with its save/restore of the pre-live controls) stays the
            // single source of truth for that transition.
            $scope.isLiveMode = false;
            stopLiveMonitoring();
        }
        $scope.selectedEnvId = envId;
        $scope.page = 0;
        // true: keep whatever time range/preset the user had selected
        // (re-applied against the new environment's own data), instead of
        // resetting back to the dialog's first-load default.
        initializeTimeRange(true);
    };

    /**
     * The interceptor SDK's own /sync cadence for the currently-selected
     * scope — Live Monitoring refreshes at exactly this rate, same
     * principle as AgentStatsDialogController#getAgentSyncInterval (match
     * the polled entity's own push interval, don't guess a constant).
     * 'All Environments' has no single interval, so it uses the FASTEST
     * of its environments' — refreshing that often is still correct for
     * every slower environment, just occasionally a no-op re-fetch.
     */
    function getInterceptorSyncInterval() {
        var envs = interceptor.environments || [];
        if ($scope.selectedEnvId !== 'all') {
            var match = envs.filter(function (e) { return e.id === $scope.selectedEnvId; })[0];
            var secs = match && match.parameters && match.parameters.sync_interval_seconds;
            return (secs && secs > 0) ? secs : 20;
        }
        var all = envs
            .map(function (e) { return e.parameters && e.parameters.sync_interval_seconds; })
            .filter(function (s) { return s && s > 0; });
        return all.length ? Math.min.apply(null, all) : 20;
    }

    // ========================================
    // METRICS SELECTION — nested avg/max/min per dimension, per the
    // InterceptorTimeSeriesStatsDTO/InterceptorTimeSeriesMetricStatsDTO
    // shape (bucket.avg_captured_rate_per_min.avg, not a flat number).
    // ========================================

    var METRIC_DEFS = {
        avg_captured_rate_per_min: { label: 'Avg Frame Receive Rate',  icon: 'videocam',              color: '#2196f3', textColor: '#1565c0', axis: 'y-frame-rate' },
        avg_uploaded_rate_per_min: { label: 'Avg Frame Uploaded Rate', icon: 'send',                  color: '#4caf50', textColor: '#2e7d32', axis: 'y-frame-rate' },
        avg_filtered_rate_per_min: { label: 'Avg Frame Filtered Rate', icon: 'filter_alt',            color: '#ff9800', textColor: '#e65100', axis: 'y-frame-rate' },
        avg_dropped_rate_per_min:  { label: 'Avg Frame Dropped Rate',  icon: 'remove_circle_outline', color: '#e53935', textColor: '#c62828', axis: 'y-frame-rate' },
        avg_receive_rate_kb_min: { label: 'Avg Size Receive Rate', icon: 'cloud_download', color: '#8e24aa', textColor: '#6a1b7a', axis: 'y-rate' },
        avg_upload_rate_kb_min:  { label: 'Avg Size Upload Rate',  icon: 'cloud_upload',   color: '#00897b', textColor: '#00695c', axis: 'y-rate' },
        agent_ping_time_ms:   { label: 'Agent Ping Time', icon: 'speed',                  color: '#3949ab', textColor: '#283593', axis: 'y-ms' },
        load_pressure:        { label: 'Pressure',        icon: 'compress',               color: '#d81b60', textColor: '#ad1457', axis: 'y-pressure' },
        active_worker_count:  { label: 'Active Workers',       icon: 'group_work', color: '#00acc1', textColor: '#00707f', axis: 'y-count' },
        transmit_batch_size:  { label: 'Transmit Batch Size',  icon: 'dynamic_feed', color: '#7cb342', textColor: '#558b2f', axis: 'y-count' }
    };
    $scope.metricKeys = Object.keys(METRIC_DEFS);

    // Reasonable default — captured→uploaded is the core pipeline most
    // users want to see first; the rest is one click away.
    $scope.metrics = {
        avg_captured_rate_per_min: true,
        avg_uploaded_rate_per_min: true,
        avg_filtered_rate_per_min: false,
        avg_dropped_rate_per_min: false,
        avg_receive_rate_kb_min: false,
        avg_upload_rate_kb_min: false,
        agent_ping_time_ms: false,
        load_pressure: false,
        active_worker_count: false,
        transmit_batch_size: false
    };

    $scope.hasAnyMetricSelected = function () {
        return $scope.metricKeys.some(function (k) { return $scope.metrics[k]; });
    };

    $scope.allMetricsSelected = function () {
        return $scope.metricKeys.every(function (k) { return $scope.metrics[k]; });
    };

    $scope.selectAllMetrics = function () {
        $scope.metricKeys.forEach(function (k) { $scope.metrics[k] = true; });
        $scope.selectedMetrics = $scope.metricKeys.slice();
        $scope.renderChart();
    };

    $scope.clearAllMetrics = function () {
        $scope.metricKeys.forEach(function (k) { $scope.metrics[k] = false; });
        $scope.selectedMetrics = [];
        $scope.renderChart();
    };

    // ===== "Show metrics" dropdown (2026-09-20 — replaces the row of
    // individual toggle chips, one per dimension, with an md-select multiple:
    // same rationale and pattern as AgentStatsDialogController's own dropdown
    // — 8 dimensions don't fit tidily as a row of chips. selectedMetrics is
    // the source of truth the dropdown itself reads/writes; $scope.metrics
    // (flag-per-key map) is kept in sync alongside it since the rest of this
    // controller (buildOrUpdateDataset filtering, hasAnyMetricSelected, etc.)
    // already reads that shape. =====
    $scope.selectedMetrics = $scope.metricKeys.filter(function (k) { return $scope.metrics[k]; });

    // metricOptions()/thresholdOptions() (below) run every digest from
    // ng-repeat — returning a fresh array of fresh {key,label} objects each
    // time means those objects never have stable identity, so ngRepeat's
    // $watchCollection sees "changed" forever and Angular throws
    // $rootScope:infdig. Cache by the underlying key list and only rebuild
    // when it actually changes, so the same references come back otherwise.
    function arraysEqual(a, b) {
        if (a.length !== b.length) return false;
        for (var i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    }

    var _metricOptionsCache = { keys: null, list: null };
    $scope.metricOptions = function () {
        var keys = $scope.metricKeys;
        if (_metricOptionsCache.keys && arraysEqual(_metricOptionsCache.keys, keys)) {
            return _metricOptionsCache.list;
        }
        var list = keys.map(function (k) {
            return { key: k, label: METRIC_DEFS[k].label };
        });
        _metricOptionsCache = { keys: keys, list: list };
        return list;
    };

    $scope.onMetricsSelectionChanged = function () {
        $scope.metricKeys.forEach(function (k) {
            $scope.metrics[k] = $scope.selectedMetrics.indexOf(k) !== -1;
        });
        $scope.renderChart();
    };

    $scope.metricsSelectedSummary = function () {
        var total = $scope.metricKeys.length;
        var count = $scope.selectedMetrics.length;
        if (count === 0) return 'No metrics selected';
        if (count === total) return 'All metrics';
        return count + ' of ' + total + ' metrics';
    };

    // ========================================
    // SLA THRESHOLD OVERLAYS (2026-09-20 parity pass) — this interceptor's
    // own SLA config (Pressure / Agent Latency, set on the Edit dialog's
    // SLAs tab) overlaid on the chart, same SET-solid/RESET-dashed treatment
    // as AgentStatsDialogController. Unlike Tuning parameters, monitoring_config
    // lives at the INTERCEPTOR level, not per-environment, so one fetch here
    // covers every environment scope in this dialog. The list row this dialog
    // opens from doesn't carry it, so it's fetched separately, the same way
    // EditBizInterceptorDialogController does via BizInterceptorService.getById.
    // ========================================

    $scope.monitoringConfig = {};

    BizInterceptorService.getById(interceptor.biz_interceptor_id, { skipLoader: true })
        .then(function (response) {
            var raw = (response.data && response.data.monitoring_config) || {};
            $scope.monitoringConfig = {
                pressure: raw.pressure || {},
                agentLatency: raw.agent_latency || {}
            };
        })
        .catch(function (error) {
            console.warn('MetricsExplorer: error loading monitoring config', error);
        });

    $scope.thresholds = { pressure: false, latency: false };

    // Maps each threshold key to the metric it overlays and the axis that
    // metric plots on (see METRIC_DEFS above) — load_pressure/y-pressure and
    // agent_ping_time_ms/y-ms respectively.
    var THRESHOLD_META = {
        pressure: { label: 'Pressure', metricKey: 'load_pressure', axis: 'y-pressure' },
        latency:  { label: 'Agent Latency', metricKey: 'agent_ping_time_ms', axis: 'y-ms' }
    };

    // Same colors as AgentStatsDialogController's own Pressure/Latency
    // thresholds — this is the same underlying SLA concept (Pressure) or a
    // closely related one (Agent Latency vs. Latency), so the color language
    // stays consistent across both dialogs rather than inventing a second one.
    var THRESHOLD_COLORS = {
        pressure: { set: '#00695c', reset: '#4db6ac' },
        latency:  { set: '#e65100', reset: '#ffb74d' }
    };

    $scope.hasAnyThresholdAvailable = function () {
        var mc = $scope.monitoringConfig;
        return !!((mc.pressure && mc.pressure.enabled) || (mc.agentLatency && mc.agentLatency.enabled));
    };

    // Same condition each threshold option is gated on: its own SLA
    // monitoring enabled AND its underlying metric currently shown.
    function availableThresholdKeys() {
        var mc = $scope.monitoringConfig;
        return Object.keys(THRESHOLD_META).filter(function (k) {
            var cfg = k === 'pressure' ? mc.pressure : mc.agentLatency;
            return cfg && cfg.enabled && $scope.metrics[THRESHOLD_META[k].metricKey];
        });
    }

    $scope.selectedThresholds = [];

    var _thresholdOptionsCache = { keys: null, list: null };
    $scope.thresholdOptions = function () {
        var keys = availableThresholdKeys();
        if (_thresholdOptionsCache.keys && arraysEqual(_thresholdOptionsCache.keys, keys)) {
            return _thresholdOptionsCache.list;
        }
        var list = keys.map(function (k) {
            return { key: k, label: THRESHOLD_META[k].label };
        });
        _thresholdOptionsCache = { keys: keys, list: list };
        return list;
    };

    $scope.onThresholdsSelectionChanged = function () {
        Object.keys($scope.thresholds).forEach(function (k) {
            $scope.thresholds[k] = $scope.selectedThresholds.indexOf(k) !== -1;
        });
        $scope.renderChart();
    };

    $scope.thresholdsSelectedSummary = function () {
        var total = availableThresholdKeys().length;
        var count = $scope.selectedThresholds.length;
        if (count === 0) return 'No thresholds selected';
        if (count === total) return 'All thresholds';
        return count + ' of ' + total + ' thresholds';
    };

    // Two flat points spanning the current chart's time domain — same
    // technique as AgentStatsDialogController's getThresholdLineData/
    // makeThresholdLine, reused by key in datasetsByKey (below) so Chart.js's
    // per-dataset animation state survives across renders instead of popping
    // in fresh every tick.
    function buildThresholdDataset(key, fromTs, toTs) {
        var meta = THRESHOLD_META[key];
        var mc = $scope.monitoringConfig;
        var cfg = key === 'pressure' ? mc.pressure : mc.agentLatency;
        var setColor = THRESHOLD_COLORS[key];

        function line(suffix, dashed, yValue, color, label) {
            var dsKey = key + '-' + suffix;
            var ds = datasetsByKey[dsKey];
            var data = [{ x: fromTs, y: yValue }, { x: toTs, y: yValue }];
            if (ds) {
                ds.data = data;
            } else {
                ds = datasetsByKey[dsKey] = {
                    label: label, data: data, borderColor: color, backgroundColor: 'transparent',
                    borderWidth: dashed ? 1.5 : 2.5, borderDash: dashed ? [6, 4] : [],
                    pointRadius: 0, pointHoverRadius: 0, pointHitRadius: 0,
                    fill: false, tension: 0, yAxisID: meta.axis
                };
            }
            return ds;
        }

        return [
            line('set', false, (cfg.set && cfg.set.threshold) || 0, setColor.set, meta.label + ' SET threshold'),
            line('reset', true, (cfg.reset && cfg.reset.threshold) || 0, setColor.reset, meta.label + ' RESET threshold')
        ];
    }

    // ===== DROPDOWN PANEL POSITIONING (Show metrics / SLA thresholds) =====
    // Same Angular Material 1.2.2 md-select[multiple] positioning fix as
    // AgentStatsDialogController — see that file's own comment on this block
    // for the full explanation of why it's needed and how it works. Kept
    // duplicated rather than factored into a shared service: each of these
    // dialog controllers is already self-contained (own pagination, own
    // slider setup, etc.), and this is a small, self-contained fix.
    $scope.rememberDropdownTrigger = function ($event) {
        _lastDropdownTrigger = $event.currentTarget;
    };

    var _lastDropdownTrigger = null;
    var _dropdownPanelObserver = null;

    function repositionDropdownPanel(panelNode) {
        if (!_lastDropdownTrigger) return;
        var triggerRect = _lastDropdownTrigger.getBoundingClientRect();
        var desiredLeft = triggerRect.left;
        var maxLeft = window.innerWidth - panelNode.offsetWidth - 8;
        if (maxLeft > 0 && desiredLeft > maxLeft) desiredLeft = maxLeft;

        panelNode.style.transform = 'none';
        panelNode.style.top = (triggerRect.bottom + 4) + 'px';
        panelNode.style.left = Math.max(8, desiredLeft) + 'px';
    }

    if (typeof MutationObserver !== 'undefined') {
        _dropdownPanelObserver = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                Array.prototype.forEach.call(mutation.addedNodes || [], function (node) {
                    if (node.nodeType !== 1) return;
                    if (!node.classList || !node.classList.contains('mex-dropdown-panel')) return;

                    var settled = false;
                    var styleObserver = new MutationObserver(function () {
                        if (settled) return;
                        settled = true;
                        styleObserver.disconnect();
                        repositionDropdownPanel(node);
                    });
                    styleObserver.observe(node, { attributes: true, attributeFilter: ['style'] });

                    $timeout(function () {
                        if (settled) return;
                        settled = true;
                        styleObserver.disconnect();
                        repositionDropdownPanel(node);
                    }, 100);
                });
            });
        });
        _dropdownPanelObserver.observe(document.body, { childList: true });
    }

    // ========================================
    // CONFIG
    // ========================================

    var maxHistoryDays = ConfigurationService.getFromCache('frontend.biz-interceptor.stats.max-metrics-history-window-days') || 30;
    var minIntervalSecs = Math.min(
        (ConfigurationService.getFromCache('frontend.biz-interceptor.stats.min-metrics-aggregation-interval-mins') || 1) * 60,
        10
    );
    var maxIntervalSecs = (ConfigurationService.getFromCache('frontend.biz-interceptor.stats.max-metrics-aggregation-interval-mins') || 60) * 60;
    var defaultIntervalSecs = (ConfigurationService.getFromCache('frontend.biz-interceptor.stats.default-metrics-aggregation-interval-mins') || 15) * 60;
    var defaultPageSize = ConfigurationService.getFromCache('frontend.biz-interceptor.stats.default-metrics-page-size') || 30;

    $scope.pageSize = defaultPageSize;

    var today = new Date();
    today.setHours(23, 59, 59, 999);
    var todayTs = today.getTime();

    var maxDaysAgo = new Date();
    maxDaysAgo.setDate(maxDaysAgo.getDate() - maxHistoryDays);
    maxDaysAgo.setHours(0, 0, 0, 0);
    var maxDaysAgoTs = maxDaysAgo.getTime();

    var actualMinTimestamp = null;
    var actualMaxTimestamp = null;

    var intervalStep = 10;

    // ========================================
    // TIME RANGE SLIDER
    // ========================================

    $scope.timeRangeSlider = {
        minValue: maxDaysAgoTs,
        maxValue: todayTs,
        options: {
            floor: maxDaysAgoTs,
            ceil: todayTs,
            step: Math.round((todayTs - maxDaysAgoTs) * 0.01),
            minRange: Math.round((todayTs - maxDaysAgoTs) * 0.01),
            pushRange: true,
            draggableRange: false,
            showSelectionBar: true,
            noSwitching: true,
            enforceRange: true,
            enforceStep: false,
            disabled: true,

            translate: function (value) {
                if (!value || value < 0 || !isFinite(value)) { return 'Invalid date'; }
                var date = new Date(value);
                if (isNaN(date.getTime())) { return 'Invalid date'; }
                var sliderMin = $scope.timeRangeSlider.minValue || 0;
                var sliderMax = $scope.timeRangeSlider.maxValue || Date.now();
                var rangeDays = (sliderMax - sliderMin) / 86400000;
                if (rangeDays < 2) {
                    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                }
                return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
            },

            onEnd: function () {
                if (!$scope.timeRangeSlider.minValue || !$scope.timeRangeSlider.maxValue) { return; }
                if ($scope.timeRangeSlider.minValue >= $scope.timeRangeSlider.maxValue) { return; }
                triggerTimeRangeChangeFromSlider();
            }
        }
    };

    // ========================================
    // AGGREGATION INTERVAL SLIDER
    // ========================================

    $scope.intervalSlider = {
        value: defaultIntervalSecs,
        options: {
            floor: 10,
            ceil: maxIntervalSecs,
            step: intervalStep,
            enforceStep: true,
            showSelectionBar: true,

            translate: function (value) {
                if (value < 60) { return value + 's'; }
                if (value < 3600) { var mins = Math.round(value / 60); return mins + (mins === 1 ? ' min' : ' mins'); }
                if (value < 86400) { var hours = Math.round(value / 3600); return hours + (hours === 1 ? ' hour' : ' hours'); }
                var days = Math.round(value / 86400);
                return days + (days === 1 ? ' day' : ' days');
            },

            disabled: true,
            onEnd: function () { onIntervalChange(); }
        }
    };

    $scope.onLiveModeChange = function () {
        // ng-model already flipped $scope.isLiveMode before ng-change fires
        // — defer a tick so toggleLiveMode reads the settled value instead
        // of racing the same digest that's still applying it.
        $timeout(function () { $scope.toggleLiveMode(); }, 0);
    };

    var savedLiveState = null;

    $scope.toggleLiveMode = function () {
        if ($scope.isLiveMode) {
            savedLiveState = {
                intervalValue: $scope.intervalSlider.value,
                pageSize: $scope.pageSize,
                timeRange: { min: $scope.timeRangeSlider.minValue, max: $scope.timeRangeSlider.maxValue },
                timePreset: $scope.activeTimePreset
            };

            var syncIntervalSecs = getInterceptorSyncInterval();
            $scope.liveRefreshIntervalSecs = syncIntervalSecs;

            $scope.timeRangeSlider.options.disabled = true;
            $scope.intervalSlider.options.disabled = true;
            $scope.intervalSlider.options.floor = Math.min(10, syncIntervalSecs);
            $scope.pageSize = LIVE_POINT_COUNT;
            $scope.activeTimePreset = null;
            $scope.page = 0;

            liveWindowMs = LIVE_POINT_COUNT * syncIntervalSecs * 1000;

            if (actualMaxTimestamp) {
                var bucketMs = syncIntervalSecs * 1000;
                var to = Math.floor(actualMaxTimestamp / bucketMs) * bucketMs + bucketMs;
                var from = Math.max(actualMinTimestamp || 0, to - liveWindowMs);
                $scope.timeRangeSlider.minValue = from;
                $scope.timeRangeSlider.maxValue = to;
            }

            $timeout(function () {
                // Set on the next digest, after the floor/disabled options
                // above have already settled — assigning it in the same
                // synchronous pass as a floor change let rzslider's own
                // watcher clobber it back to whatever value was showing
                // before Live Monitoring was turned on (same reason
                // AgentStatsDialogController#toggleLiveMode sets its
                // interval value inside this same $timeout, not before it).
                $scope.intervalSlider.value = syncIntervalSecs;
                $scope.$broadcast('rzSliderForceRender');
                $scope.loadMetrics();
                startLiveMonitoring();
            }, 0);
        } else {
            stopLiveMonitoring();
            liveWindowMs = null;
            $scope.timeRangeSlider.options.disabled = false;
            $scope.intervalSlider.options.disabled = false;
            $scope.intervalSlider.options.floor = 10;

            if (savedLiveState) {
                $scope.intervalSlider.value = savedLiveState.intervalValue;
                $scope.pageSize = savedLiveState.pageSize;
                $scope.timeRangeSlider.minValue = savedLiveState.timeRange.min;
                $scope.timeRangeSlider.maxValue = savedLiveState.timeRange.max;
                $scope.activeTimePreset = savedLiveState.timePreset;
                savedLiveState = null;
            }

            $timeout(function () {
                $scope.$broadcast('rzSliderForceRender');
                $scope.page = 0;
                $scope.loadMetrics();
            }, 0);
        }
    };

    function startLiveMonitoring() {
        var syncSecs = getInterceptorSyncInterval();
        var configuredGrace = Number(ConfigurationService.getFromCache('frontend.biz-interceptor.stats.live-mode-refresh-grace-seconds'));
        var graceSecs = Number.isFinite(configuredGrace) && configuredGrace >= 0 ? configuredGrace : 0;
        var refreshSecs = syncSecs + graceSecs;
        $scope.liveRefreshIntervalSecs = refreshSecs;
        if (liveMonitoringInterval) { clearInterval(liveMonitoringInterval); }

        liveMonitoringInterval = setInterval(function () {
            if (!$scope.isLiveMode) { stopLiveMonitoring(); return; }
            // true — this is the silent background tick, not a user action
            // (pagination, filter/interval change) — see loadMetrics' own
            // skipLoader param comment for why only this call site passes it.
            try {
                $scope.$apply(function () { $scope.loadMetrics(true); });
            } catch (e) {
                // Already mid-digest (e.g. a slider drag callback triggered
                // this same tick) — call directly rather than let $apply
                // throw "$digest already in progress".
                $scope.loadMetrics(true);
            }
        }, refreshSecs * 1000);
    }

    function stopLiveMonitoring() {
        if (chartInstance) LiveChartViewport.cancel(chartInstance);
        if (liveMonitoringInterval) {
            clearInterval(liveMonitoringInterval);
            liveMonitoringInterval = null;
        }
    }

    // True only when this interceptor/environment has never reported any
    // time-series data at all (initializeTimeRange found no min/max) — as
    // opposed to just having no data in the currently-selected window,
    // where the controls stay usable so the user can go looking for a
    // window that does have data. Every control that only makes sense once
    // there's something to query against gets disabled in this state.
    $scope.noDataAtAll = true;

    // ========================================
    // TIME RANGE INITIALIZATION
    // ========================================

    // preserveSelection: true when this is re-fetching for a DIFFERENT
    // scope the user picked (an environment switch — see $scope.selectEnv),
    // as opposed to the dialog's very first load (nothing to preserve yet).
    // Without it, switching environments used to always snap back to "last
    // hour" (or the whole range, if under an hour), silently discarding
    // whatever time range/preset the user had actually selected.
    function initializeTimeRange(preserveSelection) {
        $scope.timeRangeLoading = true;

        var previousPreset = preserveSelection ? $scope.activeTimePreset : null;
        var previousMin = preserveSelection ? $scope.timeRangeSlider.minValue : null;
        var previousMax = preserveSelection ? $scope.timeRangeSlider.maxValue : null;

        BizInterceptorService.getTimeSeriesTimeRange(interceptor.biz_interceptor_id, currentEnvironmentId())
            .then(function (response) {
                var data = response.data;

                if (!data || !data.min_timestamp || !data.max_timestamp || data.min_timestamp >= data.max_timestamp) {
                    $scope.noDataMessage = 'No time-series data available yet';
                    $scope.aggregatedData = [];
                    $scope.noDataAtAll = true;
                    $scope.timeRangeSlider.options.disabled = true;
                    $scope.intervalSlider.options.disabled = true;
                    $scope.timeRangeLoading = false;
                    $scope.loading = false;
                    return;
                }

                $scope.noDataAtAll = false;
                $scope.intervalSlider.options.disabled = false;

                actualMinTimestamp = data.min_timestamp;
                actualMaxTimestamp = data.max_timestamp;

                $scope.timeRangeSlider.options.floor = actualMinTimestamp;
                $scope.timeRangeSlider.options.ceil = actualMaxTimestamp;

                var rangeMs = actualMaxTimestamp - actualMinTimestamp;
                var step = Math.max(60000, Math.round(rangeMs * 0.01));
                $scope.timeRangeSlider.options.step = step;
                $scope.timeRangeSlider.options.minRange = step;

                if (previousPreset) {
                    // A preset (Last 10 Min / Last Hour / N days) was
                    // active — re-derive the SAME preset against this
                    // scope's own max timestamp instead of losing it.
                    var range = computePresetRange(previousPreset);
                    $scope.timeRangeSlider.minValue = range.from;
                    $scope.timeRangeSlider.maxValue = range.to;
                    $scope.activeTimePreset = previousPreset;
                } else if (previousMin && previousMax) {
                    // A custom drag was active — keep the same absolute
                    // window, clamped into whatever this scope's data
                    // actually spans.
                    $scope.timeRangeSlider.minValue = Math.max(actualMinTimestamp, previousMin);
                    $scope.timeRangeSlider.maxValue = Math.min(actualMaxTimestamp, previousMax);
                    if ($scope.timeRangeSlider.minValue >= $scope.timeRangeSlider.maxValue) {
                        // Didn't survive clamping (this scope's data doesn't
                        // overlap the old window at all) — fall back to the
                        // usual default instead of an inverted range.
                        $scope.timeRangeSlider.minValue = actualMinTimestamp;
                        $scope.timeRangeSlider.maxValue = actualMaxTimestamp;
                    }
                    $scope.activeTimePreset = null;
                } else {
                    var oneHourMs = 60 * 60 * 1000;
                    if (rangeMs > oneHourMs) {
                        $scope.timeRangeSlider.minValue = actualMaxTimestamp - oneHourMs;
                        $scope.timeRangeSlider.maxValue = actualMaxTimestamp;
                        $scope.activeTimePreset = 'hour';
                    } else {
                        $scope.timeRangeSlider.minValue = actualMinTimestamp;
                        $scope.timeRangeSlider.maxValue = actualMaxTimestamp;
                    }
                }

                $scope.timeRangeSlider.options.disabled = false;

                $timeout(function () {
                    $scope.$broadcast('rzSliderForceRender');
                    $scope.timeRangeLoading = false;
                    $scope.page = 'LAST';
                    $scope.loadMetrics();
                }, 50);
            })
            .catch(function (error) {
                console.error('[MetricsExplorer] Error fetching time range:', error);
                $scope.timeRangeLoading = false;
                $scope.loading = false;
                $scope.noDataMessage = 'Error loading time range';
            });
    }

    // ========================================
    // AVAILABLE INTERVALS — narrows the interval preset choices to what
    // makes sense for the current time-range span, same buckets as
    // AgentStatsDialogController#getAvailableIntervals.
    // ========================================

    function getAvailableIntervals() {
        var rangeMs = $scope.timeRangeSlider.maxValue - $scope.timeRangeSlider.minValue;
        var rangeMinutes = rangeMs / 60000;
        var rangeHours = rangeMinutes / 60;
        var rangeDays = rangeHours / 24;

        var candidates;
        if (rangeMinutes <= 10) { candidates = [10, 60, 300]; }
        else if (rangeHours < 1) { candidates = [10, 60, 300, 900, 1800]; }
        else if (rangeHours <= 24) { candidates = [60, 300, 900, 1800, 3600]; }
        else if (rangeDays < 6.0) { candidates = [1800, 3600, 86400]; }
        else { candidates = [60, 300, 900, 1800, 3600, 86400]; }

        var available = candidates.filter(function (s) { return s <= maxIntervalSecs; });
        if (available.length === 0) { available.push(candidates[0]); }
        return available;
    }

    $scope.isIntervalAvailable = function (seconds) {
        return getAvailableIntervals().indexOf(seconds) > -1;
    };

    function adjustIntervalIfNeeded() {
        var available = getAvailableIntervals();
        if (available.indexOf($scope.intervalSlider.value) === -1) {
            var closest = available[0];
            var minDiff = Math.abs($scope.intervalSlider.value - closest);
            available.forEach(function (iv) {
                var diff = Math.abs($scope.intervalSlider.value - iv);
                if (diff < minDiff) { minDiff = diff; closest = iv; }
            });
            $scope.intervalSlider.value = closest;
            return true;
        }
        return false;
    }

    var timeRangeChangeTimeout = null;
    var intervalChangeTimeout = null;

    function triggerTimeRangeChangeFromPreset() {
        if (timeRangeChangeTimeout) { $timeout.cancel(timeRangeChangeTimeout); }
        $scope.isChangingInterval = true;
        timeRangeChangeTimeout = $timeout(function () {
            if ($scope.page !== 'LAST') { $scope.page = 0; }
            $scope.loadMetrics();
        }, 500);
    }

    function triggerTimeRangeChangeFromSlider() {
        if (timeRangeChangeTimeout) { $timeout.cancel(timeRangeChangeTimeout); }
        $scope.isChangingInterval = true;
        $scope.activeTimePreset = null;

        var adjusted = adjustIntervalIfNeeded();

        timeRangeChangeTimeout = $timeout(function () {
            $scope.page = 0;
            if (adjusted) {
                $scope.$broadcast('rzSliderForceRender');
                $timeout(function () { $scope.loadMetrics(); }, 100);
            } else {
                $scope.loadMetrics();
            }
        }, 500);
    }

    function onIntervalChange() {
        if (intervalChangeTimeout) { $timeout.cancel(intervalChangeTimeout); }
        $scope.isChangingInterval = true;
        intervalChangeTimeout = $timeout(function () {
            $scope.page = 0;
            $scope.loadMetrics();
        }, 300);
    }

    // ========================================
    // PRESETS
    // ========================================

    $scope.hasCustomTimeRange = function () {
        if (!actualMinTimestamp || !actualMaxTimestamp) { return false; }
        return $scope.timeRangeSlider.minValue !== actualMinTimestamp ||
            $scope.timeRangeSlider.maxValue !== actualMaxTimestamp;
    };

    $scope.resetTimeRange = function () {
        if (!actualMinTimestamp || !actualMaxTimestamp) { return; }
        $scope.timeRangeSlider.minValue = actualMinTimestamp;
        $scope.timeRangeSlider.maxValue = actualMaxTimestamp;
        $scope.activeTimePreset = null;
        $timeout(function () { $scope.$broadcast('rzSliderForceRender'); }, 50);
        triggerTimeRangeChangeFromSlider();
    };

    // Pure from/to computation for a given preset key ('min' | 'hour' | a
    // number of days) against the CURRENT actualMinTimestamp/actualMaxTimestamp
    // — shared by the three preset buttons below and by initializeTimeRange's
    // own "re-apply whatever preset was active" logic when switching
    // environments (see the preserveSelection param there), so both paths
    // compute the exact same range for the same preset instead of drifting
    // apart.
    function computePresetRange(presetKey) {
        var to = actualMaxTimestamp;
        var from;
        if (presetKey === 'min') {
            from = Math.max(actualMinTimestamp, to - (10 * 60 * 1000));
        } else if (presetKey === 'hour') {
            from = Math.max(actualMinTimestamp, to - (60 * 60 * 1000));
        } else {
            var days = Number(presetKey);
            if (days === 1) {
                from = Math.max(actualMinTimestamp, to - (24 * 60 * 60 * 1000));
            } else {
                var toDate = new Date(to);
                toDate.setHours(23, 59, 59, 999);
                var fromDate = new Date(toDate);
                fromDate.setDate(fromDate.getDate() - days);
                fromDate.setHours(0, 0, 0, 0);
                from = Math.max(actualMinTimestamp, fromDate.getTime());
                to = Math.min(actualMaxTimestamp, toDate.getTime());
            }
        }
        return { from: from, to: to };
    }

    function applyPreset(fromTimestamp, toTimestamp, presetKey) {
        $scope.timeRangeSlider.minValue = fromTimestamp;
        $scope.timeRangeSlider.maxValue = toTimestamp;
        $scope.activeTimePreset = presetKey;

        $timeout(function () {
            $scope.$broadcast('rzSliderForceRender');
            $timeout(function () {
                var adjusted = adjustIntervalIfNeeded();
                $scope.page = 'LAST';
                if (adjusted) {
                    $scope.$broadcast('rzSliderForceRender');
                    $timeout(function () { triggerTimeRangeChangeFromPreset(); }, 100);
                } else {
                    triggerTimeRangeChangeFromPreset();
                }
            }, 100);
        }, 50);
    }

    $scope.setLast10MinPreset = function () {
        if (!actualMaxTimestamp) { return; }
        var range = computePresetRange('min');
        applyPreset(range.from, range.to, 'min');
    };

    $scope.setLastHourPreset = function () {
        if (!actualMaxTimestamp) { return; }
        var range = computePresetRange('hour');
        applyPreset(range.from, range.to, 'hour');
    };

    $scope.setTimeRangePreset = function (days) {
        if (!actualMinTimestamp || !actualMaxTimestamp) { return; }
        var range = computePresetRange(days);
        applyPreset(range.from, range.to, days);
    };

    $scope.setIntervalPreset = function (seconds) {
        if (!$scope.isIntervalAvailable(seconds)) { return; }

        var effective = Math.max(minIntervalSecs, Math.min(seconds, maxIntervalSecs));
        var step = ($scope.intervalSlider.options && $scope.intervalSlider.options.step) || 10;
        effective = Math.round(effective / step) * step;
        var floor = ($scope.intervalSlider.options && $scope.intervalSlider.options.floor) || 10;
        if (effective < floor) { effective = floor; }

        if ($scope.intervalSlider.value === effective) { return; }

        $scope.intervalSlider.value = effective;
        $scope.isChangingInterval = true;

        $timeout(function () {
            $scope.$broadcast('rzSliderForceRender');
            $timeout(function () { $scope.isChangingInterval = false; }, 0);
        }, 0);

        if (intervalChangeTimeout) { $timeout.cancel(intervalChangeTimeout); }
        $scope.page = 0;
        $scope.loadMetrics();
    };

    // ========================================
    // DATA LOADING
    // ========================================

    // skipLoader — true only for Live Monitoring's own silent background
    // tick (see startLiveMonitoring above), so its periodic re-fetch
    // doesn't flash the global LoadingHttpInterceptor spinner. Every other
    // caller (pagination, filter/interval changes, the dialog's initial
    // load) leaves this undefined/false and keeps the spinner, since those
    // are real user-initiated actions. $scope.isLoadingChart (this
    // dialog's own local chart-area spinner) is untouched either way.
    $scope.loadMetrics = function (skipLoader) {
        $scope.isLoadingChart = !($scope.isLiveMode && chartInstance);
        $scope.noDataMessage = '';

        // Live Monitoring re-anchors the sliding window to the SERVER's
        // latest ingested timestamp on every tick (not the client clock —
        // this dialog may have been open for a while, or the client clock
        // may drift), same principle as AgentStatsDialogController#loadMetrics.
        if ($scope.isLiveMode) {
            BizInterceptorService.getTimeSeriesTimeRange(interceptor.biz_interceptor_id, currentEnvironmentId(), skipLoader)
                .then(function (response) {
                    var data = response.data;
                    if (data && data.max_timestamp) {
                        actualMaxTimestamp = data.max_timestamp;
                        var windowMs = liveWindowMs || (LIVE_POINT_COUNT * getInterceptorSyncInterval() * 1000);
                        // The API uses [start, end): include the latest sample
                        // in its complete, stable bucket instead of excluding it.
                        var bucketMs = getInterceptorSyncInterval() * 1000;
                        var to = Math.floor(actualMaxTimestamp / bucketMs) * bucketMs + bucketMs;
                        var from = Math.max(actualMinTimestamp || 0, to - windowMs);
                        $scope.timeRangeSlider.minValue = from;
                        $scope.timeRangeSlider.maxValue = to;
                    }
                    loadMetricsInternal(skipLoader);
                })
                .catch(function () { loadMetricsInternal(skipLoader); });
            return;
        }

        loadMetricsInternal(skipLoader);
    };

    function loadMetricsInternal(skipLoader) {
        var startTs = $scope.timeRangeSlider.minValue;
        var endTs = $scope.timeRangeSlider.maxValue;
        var intervalSeconds = $scope.isLiveMode ? getInterceptorSyncInterval() : $scope.intervalSlider.value;
        var wantsLastPage = $scope.page === 'LAST';
        var requestPage = wantsLastPage ? 0 : $scope.page;

        if (!startTs || !endTs || startTs >= endTs) {
            $scope.noDataMessage = 'Invalid time range';
            $scope.isLoadingChart = false;
            $scope.isChangingInterval = false;
            $scope.loading = false;
            return;
        }

        BizInterceptorService.getAggregatedTimeSeries(
            interceptor.biz_interceptor_id, currentEnvironmentId(), startTs, endTs, intervalSeconds, requestPage, $scope.pageSize, skipLoader
        ).then(function (response) {
            if (response.status === 204 || !response.data) {
                $scope.noDataMessage = 'No metrics available for this time range';
                $scope.aggregatedData = [];
                $scope.totalPages = 0;
                $scope.totalIntervals = 0;
                $scope.page = 0;
                return null;
            }

            $scope.totalPages = response.data.total_pages || 0;
            $scope.totalIntervals = response.data.total_elements || 0;

            if (wantsLastPage && $scope.totalPages > 0) {
                var lastPage = $scope.totalPages - 1;
                $scope.page = lastPage;
                return BizInterceptorService.getAggregatedTimeSeries(
                    interceptor.biz_interceptor_id, currentEnvironmentId(), startTs, endTs, intervalSeconds, lastPage, $scope.pageSize, skipLoader
                ).then(function (lastResp) {
                    if (lastResp.status === 204 || !lastResp.data) {
                        $scope.noDataMessage = 'No metrics available';
                        $scope.aggregatedData = [];
                        return;
                    }
                    $scope.aggregatedData = lastResp.data.data || [];
                    if ($scope.aggregatedData.length) { $scope.renderChart(); }
                    else { $scope.noDataMessage = 'No metrics found'; }
                });
            }

            $scope.aggregatedData = response.data.data || [];
            if (wantsLastPage) { $scope.page = 0; }
            if ($scope.aggregatedData.length) { $scope.renderChart(); }
            else { $scope.noDataMessage = 'No metrics found'; }
            return null;
        }).catch(function (error) {
            console.error('[MetricsExplorer] Error fetching metrics:', error);
            $scope.noDataMessage = (error && error.status === 204) ? 'No metrics available for this time range' : 'Error loading metrics';
            $scope.aggregatedData = [];
            $scope.totalIntervals = 0;
            $scope.totalPages = 0;
            $scope.page = 0;
        }).finally(function () {
            $scope.loading = false;
            $scope.isLoadingChart = false;
            $scope.isChangingInterval = false;
            $timeout(function () { $scope.$broadcast('rzSliderForceRender'); }, 100);
        });
    };

    // ========================================
    // PAGINATION ("Resolution" — points-per-page)
    // ========================================

    $scope.firstPage = function () { if ($scope.page !== 0) { $scope.page = 0; $scope.loadMetrics(); } };
    $scope.lastPage = function () { if ($scope.page < $scope.totalPages - 1) { $scope.page = $scope.totalPages - 1; $scope.loadMetrics(); } };
    $scope.nextPage = function () { if ($scope.page < $scope.totalPages - 1) { $scope.page++; $scope.loadMetrics(); } };
    $scope.prevPage = function () { if ($scope.page > 0) { $scope.page--; $scope.loadMetrics(); } };

    $scope.changePageSize = function (newSize) {
        if ($scope.pageSize === newSize) { return; }
        $scope.pageSize = newSize;
        $scope.page = 0;
        $scope.loadMetrics();
    };

    // ========================================
    // CHART RENDERING
    // ========================================

    var chartInstance = null;

    // Keyed by metric key, reused across renders — Chart.js stashes each
    // dataset's animation/meta state ON the dataset object itself
    // (dataset._meta). Handing it a brand-new object every tick (even with
    // identical content) means that state doesn't exist yet, so it has
    // nothing to interpolate FROM and each point pops in fresh instead of
    // sliding smoothly — the actual cause of the "re-renders" look in Live
    // Monitoring. Reusing the same object and only mutating `.data` in
    // place (Agent's fixed-index dataset pattern, adapted here since which
    // metrics are visible is dynamic rather than fixed) keeps that meta
    // state alive across ticks so update() can animate between old and new
    // point positions instead of resetting.
    var datasetsByKey = {};

    function buildOrUpdateDataset(key) {
        var def = METRIC_DEFS[key];
        var data = $scope.aggregatedData.map(function (bucket) {
            var m = bucket[key];
            var avg = (m && m.avg !== null && m.avg !== undefined) ? m.avg : null;
            return { x: bucket.interval_start, y: avg };
        }).filter(function (p) { return p.y !== null; });

        var ds = datasetsByKey[key];
        if (ds) {
            ds.data = data;
        } else {
            ds = datasetsByKey[key] = {
                label: def.label,
                data: data,
                borderColor: def.color,
                backgroundColor: def.color + '1a',
                pointBackgroundColor: def.color,
                pointBorderColor: def.color,
                pointRadius: 0, pointHoverRadius: 5, pointHitRadius: 10,
                borderJoinStyle: 'bevel', borderCapStyle: 'butt',
                borderWidth: 2, fill: false, tension: 0.35,
                yAxisID: def.axis
            };
        }
        return ds;
    }

    // For the avg_receive_rate_kb_min/avg_upload_rate_kb_min axis — values
    // already arrive in KB/min.
    function formatRateTick(value) {
        if (value < 1024) { return value.toFixed(1) + ' KB/min'; }
        return (value / 1024).toFixed(2) + ' MB/min';
    }

    function getTimeUnit(intervalSeconds) {
        if (intervalSeconds < 60) { return 'second'; }
        var mins = intervalSeconds / 60;
        if (mins <= 5) { return 'minute'; }
        if (mins <= 60) { return 'hour'; }
        return 'day';
    }

    function getMaxTicksLimit(count, intervalSeconds) {
        var mins = intervalSeconds / 60;
        if (count <= 10) { return count; }
        if (mins <= 5) { return Math.min(count, 25); }
        if (mins <= 30) { return Math.min(count, 40); }
        if (count <= 50) { return count; }
        return 40;
    }

    $scope.renderChart = function () {
        if ($scope.isLiveMode && chartInstance) LiveChartViewport.capture(chartInstance);
        var canvas = document.getElementById('interceptorMetricsChart');
        if (!canvas) { $timeout($scope.renderChart, 100); return; }
        var container = canvas.parentElement;
        if (!container || container.offsetParent === null) { $timeout($scope.renderChart, 100); return; }
        var ctx = canvas.getContext('2d');

        var datasets = $scope.metricKeys.filter(function (k) { return $scope.metrics[k]; }).map(buildOrUpdateDataset);

        // Query through the end of the latest bucket, but draw only the
        // timestamps with visible data so the future bucket edge is not blank.
        var visibleXs = datasets.reduce(function (xs, ds) {
            return xs.concat(ds.data.filter(function (p) { return p.y != null; })
                .map(function (p) { return p.x; }));
        }, []);
        var liveMin = visibleXs.length ? Math.min.apply(null, visibleXs) : $scope.timeRangeSlider.minValue;
        var liveMax = visibleXs.length ? Math.max.apply(null, visibleXs) : $scope.timeRangeSlider.maxValue;

        // SLA threshold overlays — only for keys both checked in the
        // dropdown AND currently available (its underlying metric shown and
        // its SLA enabled); hiding a metric naturally drops its threshold
        // lines along with it rather than leaving them orphaned on a chart
        // that may no longer even display that axis.
        if ($scope.selectedThresholds.length) {
            var dataXs = $scope.aggregatedData.map(function (b) { return b.interval_start; });
            var fromTs = dataXs.length ? Math.min.apply(null, dataXs) : (Date.now() - 3600000);
            var toTs = dataXs.length ? Math.max.apply(null, dataXs) : Date.now();
            var availableNow = availableThresholdKeys();
            $scope.selectedThresholds.forEach(function (k) {
                if (availableNow.indexOf(k) === -1) return;
                datasets = datasets.concat(buildThresholdDataset(k, fromTs, toTs));
            });
        }

        var intervalSeconds = $scope.isLiveMode ? getInterceptorSyncInterval() : $scope.intervalSlider.value;
        var timeUnit = getTimeUnit(intervalSeconds);
        var maxTicks = getMaxTicksLimit($scope.aggregatedData.length, intervalSeconds);

        var anyRate = $scope.metricKeys.some(function (k) { return $scope.metrics[k] && METRIC_DEFS[k].axis === 'y-rate'; });
        var anyFrameRate = $scope.metricKeys.some(function (k) { return $scope.metrics[k] && METRIC_DEFS[k].axis === 'y-frame-rate'; });
        var anyMs = $scope.metricKeys.some(function (k) { return $scope.metrics[k] && METRIC_DEFS[k].axis === 'y-ms'; });
        var anyPressure = $scope.metricKeys.some(function (k) { return $scope.metrics[k] && METRIC_DEFS[k].axis === 'y-pressure'; });
        var anyCount = $scope.metricKeys.some(function (k) { return $scope.metrics[k] && METRIC_DEFS[k].axis === 'y-count'; });

        if (chartInstance) {
            chartInstance.data.datasets = datasets;
            chartInstance.options.scales.x.time.unit = timeUnit;
            chartInstance.options.scales.x.ticks.maxTicksLimit = maxTicks;
            chartInstance.options.scales['y-rate'].display = anyRate;
            chartInstance.options.scales['y-frame-rate'].display = anyFrameRate;
            chartInstance.options.scales['y-ms'].display = anyMs;
            chartInstance.options.scales['y-pressure'].display = anyPressure;
            chartInstance.options.scales['y-count'].display = anyCount;

            if ($scope.isLiveMode) {
                if (typeof chartInstance.setActiveElements === 'function') {
                    chartInstance.setActiveElements([]);
                }
                if (chartInstance.tooltip && typeof chartInstance.tooltip.setActiveElements === 'function') {
                    chartInstance.tooltip.setActiveElements([], { x: 0, y: 0 });
                }

                LiveChartViewport.slide(chartInstance, liveMin, liveMax);
            } else {
                chartInstance.options.scales.x.min = undefined;
                chartInstance.options.scales.x.max = undefined;
                chartInstance.options.animation = { duration: 0 };
                chartInstance.update('none');
            }
            return;
        }

        chartInstance = new Chart(ctx, {
            type: 'line',
            data: { datasets: datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: { duration: 0 },
                layout: { padding: { top: 10, bottom: 10 } },
                elements: {
                    point: { radius: 0, hoverRadius: 5, hitRadius: 10 },
                    line: { borderJoinStyle: 'bevel', borderCapStyle: 'butt' }
                },
                interaction: { mode: 'nearest', intersect: false },
                hover: { mode: 'nearest', intersect: false },
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            unit: timeUnit,
                            displayFormats: { second: 'HH:mm:ss', minute: 'MM/dd HH:mm', hour: 'MM/dd HH:mm', day: 'MM/dd' },
                            tooltipFormat: 'MMM dd, yyyy HH:mm:ss'
                        },
                        title: { display: true, text: 'Time Interval', font: { size: 12, weight: '600' } },
                        ticks: { maxRotation: 45, minRotation: 45, font: { size: 11 }, autoSkip: true, maxTicksLimit: maxTicks },
                        grid: { display: false },
                        min: ($scope.isLiveMode ? liveMin : undefined),
                        max: ($scope.isLiveMode ? liveMax : undefined)
                    },
                    'y-rate': {
                        type: 'linear', display: anyRate, position: 'right', min: 0,
                        title: { display: true, text: 'KB/min', font: { size: 12, weight: '600' } },
                        grid: { drawOnChartArea: false },
                        ticks: { font: { size: 11 }, callback: function (value) { return formatRateTick(value); } }
                    },
                    'y-frame-rate': {
                        type: 'linear', display: anyFrameRate, position: 'left', min: 0,
                        title: { display: true, text: 'Frames/min', font: { size: 12, weight: '600' } },
                        grid: { color: 'rgba(0,0,0,0.05)' },
                        ticks: { font: { size: 11 }, callback: function (value) { return value.toFixed(1) + '/min'; } }
                    },
                    'y-ms': {
                        type: 'linear', display: anyMs, position: 'right', min: 0,
                        title: { display: true, text: 'Milliseconds', font: { size: 12, weight: '600' } },
                        grid: { drawOnChartArea: false },
                        ticks: { font: { size: 11 }, callback: function (value) { return value + ' ms'; } }
                    },
                    'y-pressure': {
                        type: 'linear', display: anyPressure, position: 'right', min: 0, max: 100,
                        title: { display: true, text: 'Pressure (%)', font: { size: 12, weight: '600' } },
                        grid: { drawOnChartArea: false },
                        ticks: { font: { size: 11 }, callback: function (value) { return value + '%'; } }
                    },
                    'y-count': {
                        type: 'linear', display: anyCount, position: 'left', min: 0,
                        title: { display: true, text: 'Count', font: { size: 12, weight: '600' } },
                        grid: { color: 'rgba(0,0,0,0.05)' },
                        ticks: { font: { size: 11 }, callback: function (value) { return Math.round(value); } }
                    }
                },
                plugins: {
                    legend: {
                        display: true, position: 'top',
                        labels: { usePointStyle: true, padding: 15, font: { size: 12, weight: '600' } }
                    },
                    tooltip: {
                        backgroundColor: 'rgba(0,0,0,0.82)', padding: 12,
                        titleFont: { size: 13, weight: 'bold' }, bodyFont: { size: 12 },
                        callbacks: {
                            label: function (context) {
                                var label = context.dataset.label || '';
                                var value = context.parsed.y;
                                var isRate = context.dataset.yAxisID === 'y-rate';
                                var isFrameRate = context.dataset.yAxisID === 'y-frame-rate';
                                var isMs = context.dataset.yAxisID === 'y-ms';
                                var isPressure = context.dataset.yAxisID === 'y-pressure';
                                var isCount = context.dataset.yAxisID === 'y-count';
                                if (isRate) { return label + ': ' + formatRateTick(value); }
                                if (isFrameRate) { return label + ': ' + value.toFixed(1) + '/min'; }
                                if (isMs) { return label + ': ' + Math.round(value) + ' ms'; }
                                if (isPressure) { return label + ': ' + Math.round(value) + '%'; }
                                if (isCount) { return label + ': ' + Math.round(value); }
                                return label + ': ' + Math.round(value);
                            }
                        }
                    }
                }
            }
        });
    };

    // ========================================
    // ACTIONS / CLEANUP
    // ========================================

    $scope.close = function () {
        stopLiveMonitoring();
        if (chartInstance) { LiveChartViewport.cancel(chartInstance); chartInstance.destroy(); chartInstance = null; }
        if (intervalChangeTimeout) { $timeout.cancel(intervalChangeTimeout); }
        if (timeRangeChangeTimeout) { $timeout.cancel(timeRangeChangeTimeout); }
        $mdDialog.hide();
    };

    $scope.$on('$destroy', function () {
        stopLiveMonitoring();
        if (chartInstance) { LiveChartViewport.cancel(chartInstance); chartInstance.destroy(); chartInstance = null; }
        if (intervalChangeTimeout) { $timeout.cancel(intervalChangeTimeout); }
        if (timeRangeChangeTimeout) { $timeout.cancel(timeRangeChangeTimeout); }
        if (_dropdownPanelObserver) { _dropdownPanelObserver.disconnect(); }
    });

    // ========================================
    // INITIALIZATION
    // ========================================

    initializeTimeRange();
});
